import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import { forkJoin, Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  CompetenciaGestion,
  CompetenciaGestionService,
} from "../../../../../shared/services/competencia-gestion.service";
import {
  CompetenciaService,
  Competencia,
} from "../../../../../shared/services/competencia.service";
import { CargosService } from "../../../../../shared/services/cargos.service";

interface CargoCatalogo {
  id: number;
  nombre: string;
}

interface CompetenciaCatalogo {
  id: number;
  descripcion: string;
}

interface CompetenciaEnFila {
  id_gestion: number;
  id_competencia: number;
  descripcion: string;
}

interface FilaCargoCompetencias {
  id_cargo: number;
  cargoNombre: string;
  competenciasActivas: CompetenciaEnFila[];
  estado: number; // 1 si tiene al menos una competencia activa, 0 si ninguna
}

@Component({
  selector: "app-gestion-competencias",
  templateUrl: "./gestion-competencias.component.html",
  styleUrls: ["./gestion-competencias.component.scss"],
})
export class GestionCompetenciasComponent {
  gestionForm!: FormGroup;

  cargos: CargoCatalogo[] = [];
  competencias: CompetenciaCatalogo[] = [];
  gestionesRaw: CompetenciaGestion[] = [];

  filas: FilaCargoCompetencias[] = [];
  filasFiltradas: FilaCargoCompetencias[] = [];

  filtro = "";
  filtroEstado: number | null = null;
  pageSize = 10;
  page = 1;

  cargandoListado = false;
  cargandoCatalogos = false;
  guardando = false;

  modoEdicion = false;
  cargoEditando: number | null = null;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private competenciaGestionService: CompetenciaGestionService,
    private competenciaService: CompetenciaService,
    private cargosService: CargosService,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarCatalogosYListado();
  }

  private inicializarFormulario(): void {
    this.gestionForm = this.fb.group({
      cargo: [null, Validators.required],
      competencias: [[], Validators.required],
    });
  }

  // 🔹 Carga cargos + competencias, y luego las relaciones
  private cargarCatalogosYListado(): void {
    this.cargandoCatalogos = true;

    forkJoin({
      cargos: this.cargosService
        .getCargosActivos()
        .pipe(catchError(() => of([]))),
      competencias: this.competenciaService
        .getAll()
        .pipe(catchError(() => of([]))),
    }).subscribe(({ cargos, competencias }) => {
      this.cargos = this.normalizarLista(cargos).map((c: any) => ({
        id: Number(c.id),
        nombre: c.cargos || c.descripcion || `Cargo #${c.id}`,
      }));

      this.competencias = this.normalizarLista(competencias).map((c: any) => ({
        id: Number(c.id),
        descripcion: c.descripcion,
      }));

      this.cargandoCatalogos = false;
      this.cargarGestiones();
    });
  }

  cargarGestiones(): void {
    this.cargandoListado = true;

    this.competenciaGestionService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.gestionesRaw = this.normalizarLista(response);
        this.agrupar();
        this.cargandoListado = false;
      });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  // 🔹 Agrupa los registros por cargo, uniendo las competencias activas en una sola fila
  private agrupar(): void {
    const mapa = new Map<number, FilaCargoCompetencias>();

    this.gestionesRaw.forEach((g: any) => {
      const idCargo = Number(g.id_cargo);
      const idCompetencia = Number(g.id_competencia);

      if (!mapa.has(idCargo)) {
        const cargo = this.cargos.find((c) => c.id === idCargo);
        mapa.set(idCargo, {
          id_cargo: idCargo,
          cargoNombre: cargo?.nombre || `Cargo #${idCargo}`,
          competenciasActivas: [],
          estado: 0,
        });
      }

      if (Number(g.estado) === 1) {
        const competencia = this.competencias.find(
          (c) => c.id === idCompetencia,
        );
        mapa.get(idCargo)!.competenciasActivas.push({
          id_gestion: Number(g.id),
          id_competencia: idCompetencia,
          descripcion:
            competencia?.descripcion || `Competencia #${idCompetencia}`,
        });
      }
    });

    mapa.forEach((fila) => {
      fila.estado = fila.competenciasActivas.length > 0 ? 1 : 0;
    });

    this.filas = Array.from(mapa.values()).sort(
      (a, b) => a.id_cargo - b.id_cargo,
    );
    this.aplicarFiltro();
  }

  aplicarFiltro(): void {
    const termino = String(this.filtro || "")
      .toLowerCase()
      .trim();

    let filtradas = this.filas.filter((fila) => {
      const textoCompetencias = fila.competenciasActivas
        .map((c) => c.descripcion)
        .join(" ")
        .toLowerCase();

      const coincideTexto =
        !termino ||
        fila.cargoNombre.toLowerCase().includes(termino) ||
        textoCompetencias.includes(termino);

      const coincideEstado =
        this.filtroEstado === null || fila.estado === this.filtroEstado;

      return coincideTexto && coincideEstado;
    });

    filtradas = filtradas.sort((a, b) => {
      const estadoA = a.estado === 1 ? 0 : 1;
      const estadoB = b.estado === 1 ? 0 : 1;
      if (estadoA !== estadoB) return estadoA - estadoB;
      return a.id_cargo - b.id_cargo;
    });

    this.filasFiltradas = filtradas;
    this.page = 1;
  }

  // 🔹 Abrir modal para crear una nueva asignación cargo-competencias
  abrirModalCrear(modalRef: any): void {
    this.modoEdicion = false;
    this.cargoEditando = null;
    this.gestionForm.reset({ cargo: null, competencias: [] });
    this.gestionForm.get("cargo")?.enable();
    this.modalService.open(modalRef, { centered: true, size: "lg" });
  }

  // 🔹 Abrir modal para editar las competencias de un cargo existente
// 🔹 Abrir modal para editar: refresca cada relación del cargo por su id antes de mostrar el modal
  abrirModalEditar(modalRef: any, fila: FilaCargoCompetencias): void {
    this.modoEdicion = true;
    this.cargoEditando = fila.id_cargo;

    const idsGestion = fila.competenciasActivas.map((c) => c.id_gestion);

    if (idsGestion.length === 0) {
      this.gestionForm.reset({ cargo: fila.id_cargo, competencias: [] });
      this.gestionForm.get("cargo")?.disable();
      this.modalService.open(modalRef, { centered: true, size: "lg" });
      return;
    }

    this.cargandoListado = true;

    this.cargarDetalleCargoPorId(idsGestion).subscribe((registros) => {
      this.cargandoListado = false;

      // 🔹 Actualiza el cache local con lo que realmente devolvió el backend
      registros.forEach((registro: any) => {
        if (!registro) return;
        const idx = this.gestionesRaw.findIndex(
          (g: any) => Number(g.id) === Number(registro.id),
        );
        if (idx >= 0) {
          this.gestionesRaw[idx] = registro;
        }
      });

      const seleccionadasActivas = registros
        .filter((r: any) => r && Number(r.estado) === 1)
        .map((r: any) => Number(r.id_competencia));

      this.gestionForm.reset({
        cargo: fila.id_cargo,
        competencias: seleccionadasActivas,
      });

      this.gestionForm.get("cargo")?.disable();
      this.modalService.open(modalRef, { centered: true, size: "lg" });
    });
  }

  // 🔹 Trae cada relación cargo-competencia por su id (getById) en paralelo
  private cargarDetalleCargoPorId(ids: number[]) {
    const peticiones = ids.map((id) =>
      this.competenciaGestionService.getById(id).pipe(catchError(() => of(null))),
    );
    return forkJoin(peticiones);
  }

  // 🔹 Guarda sincronizando: crea lo nuevo, reactiva lo inactivo seleccionado, inactiva lo deseleccionado
  guardar(modal: any): void {
    if (this.gestionForm.invalid) {
      this.gestionForm.markAllAsTouched();
      Swal.fire(
        "Campos obligatorios",
        "Selecciona el cargo y al menos una competencia.",
        "warning",
      );
      return;
    }

    const raw = this.gestionForm.getRawValue();
    const idCargo = Number(raw.cargo);
    const seleccionadas: number[] = (raw.competencias || []).map((v: any) =>
      Number(v),
    );

    const registrosCargo = this.gestionesRaw.filter(
      (g: any) => Number(g.id_cargo) === idCargo,
    );

    const operaciones: Observable<any>[] = [];

    // Crear o reactivar las seleccionadas
    seleccionadas.forEach((idCompetencia) => {
      const existente: any = registrosCargo.find(
        (g: any) => Number(g.id_competencia) === idCompetencia,
      );

      if (!existente) {
        operaciones.push(
          this.competenciaGestionService
            .create({
              id_competencia: String(idCompetencia),
              id_cargo: idCargo,
              estado: 1,
            })
            .pipe(catchError(() => of(null))),
        );
      } else if (Number(existente.estado) !== 1) {
        operaciones.push(
          this.competenciaGestionService
            .activar(Number(existente.id))
            .pipe(catchError(() => of(null))),
        );
      }
    });

    // Inactivar las que estaban activas y ya no están seleccionadas
    registrosCargo
      .filter(
        (g: any) =>
          Number(g.estado) === 1 &&
          !seleccionadas.includes(Number(g.id_competencia)),
      )
      .forEach((g: any) => {
        operaciones.push(
          this.competenciaGestionService
            .inactivar(Number(g.id))
            .pipe(catchError(() => of(null))),
        );
      });

    if (operaciones.length === 0) {
      modal.close();
      Swal.fire("Sin cambios", "No hubo cambios para guardar.", "info");
      return;
    }

    this.guardando = true;
    forkJoin(operaciones).subscribe(() => {
      this.guardando = false;
      modal.close();
      Swal.fire(
        "Éxito",
        this.modoEdicion
          ? "Competencias del cargo actualizadas correctamente."
          : "Asignación creada correctamente.",
        "success",
      );
      this.cargarGestiones();
    });
  }

  // 🔹 Activar / inactivar todas las relaciones de un cargo
  cambiarEstado(fila: FilaCargoCompetencias, estado: number): void {
    const registrosCargo = this.gestionesRaw.filter(
      (g: any) => Number(g.id_cargo) === fila.id_cargo,
    );

    const objetivos =
      estado === 0
        ? fila.competenciasActivas.map((c) => c.id_gestion)
        : registrosCargo
            .filter((g: any) => Number(g.estado) !== 1)
            .map((g: any) => Number(g.id));

    if (objetivos.length === 0) {
      Swal.fire(
        "Sin cambios",
        estado === 1
          ? "Este cargo no tiene competencias inactivas para reactivar."
          : "Este cargo no tiene competencias activas para inactivar.",
        "info",
      );
      return;
    }

    const accion = estado === 1 ? "activar" : "inactivar";

    Swal.fire({
      title: `¿Deseas ${accion} las competencias de "${fila.cargoNombre}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const operaciones = objetivos.map((id) =>
        (estado === 1
          ? this.competenciaGestionService.activar(id)
          : this.competenciaGestionService.inactivar(id)
        ).pipe(catchError(() => of(null))),
      );

      forkJoin(operaciones).subscribe(() => {
        Swal.fire(
          "Éxito",
          `Competencias ${accion}das correctamente.`,
          "success",
        );
        this.cargarGestiones();
      });
    });
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }
}
